import { Building2, Check, Link2, Lock, RefreshCcw, Send, ShieldCheck, User } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { IdentitiesService } from "@/modules/dashboard/cards/services/identitiesService";
import type { Identity } from "@/modules/dashboard/cards/types/identities-api.types";
import { KycStatusPill } from "@/modules/verification/components/KycStatusPill";
import {
  ACCOUNT_CAPABILITY_KEYS,
  CAPABILITY_LABEL_KEYS,
  NEXT_STEP_CTA_KEYS,
  ONBOARDING_VERIFICATION_ROUTE,
  resolveRejectionDescription,
} from "@/modules/verification/constants/verification.constants";
import { useOrganizationVerificationStatus } from "@/modules/verification/hooks/useOrganizationVerificationStatus";
import { invalidateAccountVerification } from "@/modules/verification/services/accountVerification.memo";
import { VerificationService } from "@/modules/verification/services/verificationService";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { useTranslations } from "@/shared/lang/useTranslations";
import { countries } from "@/shared/utils/countries";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { colocaToast } from "@/shared/utils/toast";

import { SettingsRow } from "./SettingsRow";
import { SettingsDivider, SettingsSection } from "./SettingsSection";

const EMPTY = "—";

/** Country name for an ISO 3166-1 alpha-3 code, or the code itself when unknown. */
function countryName(iso3: string | null | undefined): string {
  if (!iso3) return EMPTY;
  return countries.find((country) => country.code === iso3)?.name ?? iso3;
}

/** Joins the non-empty parts of an address into one line, or the placeholder. */
function joinParts(parts: Array<string | null | undefined>): string {
  const filled = parts.filter((part): part is string => Boolean(part && part.trim()));
  return filled.length > 0 ? filled.join(", ") : EMPTY;
}

/**
 * Settings → Account → Verification: the one place an organization member can always find the account's own
 * verification (KYC or KYB), start or resume it, copy a link for whoever completes it, and read what was
 * submitted, what it unlocks and the documents it produced. Hidden from internal staff (no organization).
 *
 * Reads `GET /internal/account/verification` (memoized, shared with the Wallets banner and the dispersions
 * gate) for the verdict and `GET /internal/identities/:id` for the submitted data — the corporate identity
 * counts as the tenant's own, so a member can read it. Data is read-only here: it is what the verification
 * validated; corrections go through support.
 */
export function VerificationSection() {
  const { t, activeLanguage } = useTranslations("settings");
  const { t: tVerification } = useTranslations("verification");
  const navigate = useNavigate();
  const status = useOrganizationVerificationStatus();
  const { identityId, refresh } = status;

  const [identity, setIdentity] = useState<Identity | null>(null);
  const [identityLoading, setIdentityLoading] = useState(false);
  const [identityError, setIdentityError] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);

  const handleRequestChange = useCallback(async () => {
    setRequestLoading(true);
    try {
      await VerificationService.requestIdentityChange();
      colocaToast.success(t("verification_change_request_sent"));
    } catch {
      colocaToast.error(tVerification("error_generic_description"));
    } finally {
      setRequestLoading(false);
    }
  }, [t, tVerification]);

  useEffect(() => {
    if (!identityId) return;
    let cancelled = false;
    /* eslint-disable react-hooks-eslint/set-state-in-effect */
    setIdentityLoading(true);
    setIdentityError(false);
    /* eslint-enable react-hooks-eslint/set-state-in-effect */
    IdentitiesService.getIdentityById(identityId)
      .then(({ data }) => {
        if (!cancelled) setIdentity(data);
      })
      .catch(() => {
        // The status still renders; the submitted-data and documents rows say the data is unavailable.
        if (!cancelled) setIdentityError(true);
      })
      .finally(() => {
        if (!cancelled) setIdentityLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [identityId]);

  const handleCopyLink = useCallback(async () => {
    if (!identityId) return;
    setLinkLoading(true);
    try {
      const { data } = await VerificationService.issueLink(identityId);
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(data.verification_url);
        colocaToast.success(tVerification("link_copied"));
      } else {
        colocaToast.warning(t("verification_copy_link_unavailable"));
      }
      // A new link changes `can_resume` / `link_expires_at`; issuing also invalidated the previous one.
      invalidateAccountVerification();
      refresh();
    } catch {
      colocaToast.error(tVerification("error_generic_description"));
    } finally {
      setLinkLoading(false);
    }
  }, [identityId, refresh, t, tVerification]);

  const header = {
    id: "verificacion",
    eyebrow: t("settings_group_account"),
    title: t("verification_section_title"),
    sub: t("verification_section_sub"),
  };

  if (status.isLoading && status.data === null) {
    return (
      <SettingsSection {...header}>
        <ColocaSkeleton className="h-40 w-full" />
      </SettingsSection>
    );
  }

  if (status.hasError || status.data === null) {
    return (
      <SettingsSection {...header}>
        <ColocaAlert
          variant="destructive"
          text={t("verification_error_load")}
          actions={
            <ColocaButton size="sm" variant="default" onClick={refresh}>
              {t("verification_retry")}
            </ColocaButton>
          }
        />
      </SettingsSection>
    );
  }

  const { data } = status;
  const kycStatus = data.kyc_status;

  if (!data.has_corporate_identity) {
    return (
      <SettingsSection {...header} headerRight={<KycStatusPill status={kycStatus} />}>
        <ColocaEmpty
          surface="card"
          icon={ShieldCheck}
          title={t("verification_empty_title")}
          description={t("verification_empty_description")}
        >
          <ColocaButton variant="primary" onClick={() => navigate(ONBOARDING_VERIFICATION_ROUTE)}>
            {tVerification("banner_cta")}
          </ColocaButton>
        </ColocaEmpty>
      </SettingsSection>
    );
  }

  const isBusiness = data.verification.purpose === "KYB";
  const nextStep = data.verification.next_step;
  const ctaKey = NEXT_STEP_CTA_KEYS[nextStep];
  const canIssueLink = nextStep === "complete_capture" || nextStep === "retry";
  const dateOptions = { locale: activeLanguage, includeTime: false } as const;

  const statusHint = (() => {
    switch (kycStatus) {
      case "VERIFIED":
        return data.verified_at
          ? t("verification_verified_at", { date: formatDateTime(data.verified_at, dateOptions) })
          : null;
      case "IN_REVIEW":
        return t("verification_in_review_hint");
      case "REJECTED":
        return resolveRejectionDescription(data.rejection_reason ?? undefined, tVerification);
      case "EXPIRED":
        return t("verification_expired_hint");
      default:
        return t("verification_pending_hint");
    }
  })();

  const document = joinParts([identity?.identification_type, identity?.identification_number]);
  const address = joinParts([
    identity?.address?.address_line_1,
    identity?.address?.address_line_2,
    identity?.address?.city,
    identity?.address?.state,
    identity?.address?.country,
  ]);

  const fields: Array<[label: string, value: string]> = isBusiness
    ? [
        [t("verification_field_legal_name"), identity?.first_name?.trim() || EMPTY],
        [t("verification_field_tax_id"), document],
        [t("verification_field_country"), countryName(identity?.country)],
        [t("verification_field_business_type"), identity?.business_type ?? EMPTY],
        [
          t("verification_field_incorporation_date"),
          identity?.incorporation_date ? formatDateTime(identity.incorporation_date, dateOptions) : EMPTY,
        ],
        [t("verification_field_representative"), identity?.owner_name ?? EMPTY],
        [
          t("verification_field_representative_document"),
          joinParts([identity?.owner_id_type, identity?.owner_id_number]),
        ],
        [t("verification_field_address"), address],
        [
          t("verification_field_representative_address"),
          joinParts([
            identity?.owner_address_line1,
            identity?.owner_city,
            identity?.owner_state,
            identity?.owner_postal_code,
            identity?.owner_country_code,
          ]),
        ],
      ]
    : [
        [
          t("verification_field_name"),
          joinParts([identity?.first_name, identity?.last_name]).replace(", ", " "),
        ],
        [t("verification_field_document"), document],
        [t("verification_field_country"), countryName(identity?.country)],
        [
          t("verification_field_birthdate"),
          identity?.birthdate ? formatDateTime(identity.birthdate, dateOptions) : EMPTY,
        ],
        [t("verification_field_nationality"), identity?.nationality ?? EMPTY],
        [t("verification_field_address"), address],
      ];

  return (
    <SettingsSection {...header} headerRight={<KycStatusPill status={kycStatus} />}>
      <SettingsRow label={t("verification_type_label")}>
        <ColocaBadge icon={isBusiness ? Building2 : User}>
          {isBusiness ? t("verification_type_business") : t("verification_type_person")}
        </ColocaBadge>
      </SettingsRow>
      <SettingsDivider />

      <SettingsRow label={t("verification_status_label")} hint={statusHint ?? undefined}>
        <div className="flex flex-wrap items-center gap-2">
          <KycStatusPill status={kycStatus} />
          {ctaKey && (
            <ColocaButton size="sm" variant="primary" onClick={() => navigate(ONBOARDING_VERIFICATION_ROUTE)}>
              {tVerification(ctaKey)}
            </ColocaButton>
          )}
          {canIssueLink && (
            <ColocaButton
              size="sm"
              variant="default"
              icon={Link2}
              loading={linkLoading}
              onClick={handleCopyLink}
            >
              {t("verification_copy_link")}
            </ColocaButton>
          )}
        </div>
        {canIssueLink && (
          <p className="mt-2 text-xs text-muted-foreground">{t("verification_copy_link_hint")}</p>
        )}
      </SettingsRow>
      <SettingsDivider />

      <SettingsRow label={t("verification_data_title")} hint={t("verification_data_note")}>
        {identityError ? (
          <ColocaAlert variant="warning" text={t("verification_data_unavailable")} />
        ) : identityLoading && !identity ? (
          <ColocaSkeleton className="h-24 w-full" />
        ) : (
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            {fields.map(([label, value]) => (
              <div key={label} className="min-w-0">
                <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {label}
                </dt>
                <dd className="mt-0.5 text-[13px] text-foreground break-words">{value}</dd>
              </div>
            ))}
          </dl>
        )}
      </SettingsRow>
      <SettingsDivider />

      <SettingsRow label={t("verification_capabilities_title")}>
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {ACCOUNT_CAPABILITY_KEYS.map((capability) => {
            const allowed = data.capabilities[capability]?.allowed !== false;
            return (
              <li key={capability} className="flex items-center gap-2 text-[13px]">
                {allowed ? (
                  <Check className="size-4 shrink-0 text-primary" aria-hidden="true" />
                ) : (
                  <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                )}
                <span className={allowed ? "text-foreground" : "text-muted-foreground"}>
                  {tVerification(CAPABILITY_LABEL_KEYS[capability])}
                </span>
              </li>
            );
          })}
        </ul>
      </SettingsRow>
      <SettingsDivider />

      {/* Documents are deliberately NOT shown here: they are confidential and the section is read by every
          organization member. Switching identity re-points the organization to a NEW corporate identity
          (person or company, either way) whose verification starts over, so payouts block until it is approved. */}
      <SettingsRow label={t("verification_change_title")} hint={t("verification_change_hint")}>
        <div className="flex flex-wrap items-center gap-2">
          <ColocaButton
            size="sm"
            variant="default"
            icon={RefreshCcw}
            disabled={!data.verification.can_change_identity}
            onClick={() => setChangeOpen(true)}
          >
            {t("verification_change_identity")}
          </ColocaButton>
          {!data.verification.can_change_identity && (
            <ColocaButton
              size="sm"
              variant="ghost"
              icon={Send}
              loading={requestLoading}
              onClick={handleRequestChange}
            >
              {t("verification_change_request")}
            </ColocaButton>
          )}
        </div>
        {!data.verification.can_change_identity && data.verification.identity_change_available_at && (
          <p className="mt-2 text-xs text-muted-foreground">
            {t("verification_change_cooldown_hint", {
              date: formatDateTime(data.verification.identity_change_available_at, dateOptions),
            })}
          </p>
        )}
      </SettingsRow>

      <ColocaAlertDialog
        trigger={<span style={{ display: "none" }} />}
        open={changeOpen}
        onOpenChange={setChangeOpen}
        title={t("verification_change_dialog_title")}
        description={t("verification_change_dialog_description")}
        confirmLabel={t("verification_change_dialog_confirm")}
        cancelLabel={t("verification_change_dialog_cancel")}
        confirmVariant="destructive"
        onConfirm={() => navigate(`${ONBOARDING_VERIFICATION_ROUTE}?replace=1`)}
      />
    </SettingsSection>
  );
}
